import React, { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import TextareaAutosize from 'react-textarea-autosize';
import { Badge } from 'reactstrap';
import { InitOrganization, InitProxyPerson, Me, Person } from '../../types';
import {
  calculateDescendantsStats,
  ObjectiveWithRef as Objective,
  Tag,
} from '../../utils/models/Objective';
import LinkedObjectives from '../Objectives/LinkedObjectives';
import ObjectiveCollaboratorsOrPlaceholder from '../Objectives/ObjectiveCollaboratorsOrPlaceholder';
import ObjectiveIndicator from '../Objectives/ObjectiveIndicator';
import {
  hasNoUsefulInformation,
  useObjectiveActions,
  useObjectiveAddTag,
} from '../Objectives/ObjectiveOperations';
import ObjectivesBreakdownBar from '../Objectives/ObjectivesBreakdownBar';
import ObjectiveTags from '../Objectives/ObjectiveTags';
import ObjectiveText from '../Objectives/ObjectiveText';
import ObjectiveTypeBadge from '../Objectives/ObjectiveTypeBadge';
import {
  ObjectiveType,
  useObjectiveItemTypeContext,
} from '../Objectives/ObjectiveTypeOperations';
import {
  ObjectiveItem,
  ObjectiveItemActions,
  ObjectiveItemBody,
  ObjectiveItemHead,
  ObjectiveItemHeaderRow,
  ObjectiveItemTagsRow,
  ObjectiveItemTitle,
} from './components';
import ObjectiveItemAddControls from './ObjectiveItemAddControls';
import ObjectiveItemMoreControls from './ObjectiveItemMoreControls';
import PersonalObjectiveAddItem from './PersonalObjectiveAddItem';
import PersonalObjectiveItemCopyToNextPeriodModal from './PersonalObjectiveItemCopyToNextPeriodModal';
import PersonalObjectiveItemDeleteModal from './PersonalObjectiveItemDeleteModal';
import PersonalObjectiveItemDisconnectModal from './PersonalObjectiveItemDisconnectModal';

const EMPTY_CHILDREN_TYPE_IDS: (number | null | undefined)[] = [];

interface Props {
  objective: Objective;
  parentObjective: Objective;
  showLabelHeadings?: boolean;
  updateParentObjective?: (objective: Objective) => void;
  forceShowChildren?: boolean;
  hidden: boolean;
  currentOrganization: InitOrganization;
  currentProxyPerson: InitProxyPerson;
  me: Me;
  person: Person;
  isEditable?: boolean;
  isInPopover?: boolean;
  hideScoresAndWeight?: boolean;
  hideRelatedObjectives?: boolean;
  indent: number;
  editableObjectiveIds?: ReadonlySet<number>;
  showDescendantsCount?: boolean;
  showDescendantsBreakdown?: boolean;
  textToHighlight?: string;
  organizationObjectiveTypes: ObjectiveType[];
  userEditableObjectiveTypes: ObjectiveType[];
  // Show the type badge at the end of the title row instead of on the tags row.
  inlineTypeBadge?: boolean;
}

const PersonalObjectiveItem: FC<Props> = ({
  objective,
  parentObjective,
  updateParentObjective = () => {
    //do nothing
  },
  forceShowChildren = false,
  hidden,
  currentOrganization,
  currentProxyPerson,
  me,
  person,
  isEditable: isGlobalEditable = false,
  isInPopover = false,
  hideScoresAndWeight = false,
  hideRelatedObjectives = false,
  indent,
  showDescendantsCount = false,
  showDescendantsBreakdown = false,
  textToHighlight,
  organizationObjectiveTypes,
  userEditableObjectiveTypes,
  editableObjectiveIds,
  inlineTypeBadge = false,
}) => {
  const { formatMessage } = useIntl();
  const [value, setValue] = React.useState<Objective>({
    ...objective,
    parent: parentObjective?.id ?? null,
  });

  const { totalDescendants, descedantsStatusBreakdown, matchDescendants } =
    calculateDescendantsStats(value);

  const [showChildren, setShowChildren] = React.useState<boolean>(
    () => forceShowChildren || matchDescendants
  );

  const { isIndividual, isEditable, canAddChild } = useObjectiveItemTypeContext(
    {
      objective: value,
      organizationObjectiveTypes,
      userEditableObjectiveTypes,
      isGlobalEditable,
      editableObjectiveIds,
    }
  );

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showCopyToNextPeriodModal, setShowCopyToNextPeriodModal] =
    useState<boolean>(false);
  const [showDisconnectModal, setShowDisconnectModal] =
    useState<boolean>(false);

  const onChildChange = useCallback((objective) => {
    setValue(objective);
    if (objective.children.length == 0) {
      setShowChildren(false);
    }
  }, []);

  const {
    deleteObjective,
    disconnectFromParentObjective,
    addChild,
    addSibling,
    updateRelatedObjectives,
    updateIndicators,
    updateText,
    updateTags,
    updateObjectiveType,
    updateTextKeyDown,
  } = useObjectiveActions({
    me,
    person,
    firstDay: objective.coverage_start_date,
    lastDay: objective.coverage_end_date,
    currentOrganization,
    defaultObjectiveTypeId: undefined,
    userEditableObjectiveTypes,
  });

  const onChangeTags = useCallback(
    (tags: Tag[]) => updateTags({ tags, value, parentObjective, setValue }),
    [updateTags, value, parentObjective, setValue]
  );

  const onChangeType = useCallback(
    (objectiveType: number | null) =>
      updateObjectiveType({
        objectiveType,
        value,
        parentObjective,
        setValue,
        updateParentObjective,
      }),
    [
      updateObjectiveType,
      value,
      parentObjective,
      setValue,
      updateParentObjective,
    ]
  );

  const childrenTypeIds = useMemo(
    () =>
      value.children?.map((c) => c.objective_type) ?? EMPTY_CHILDREN_TYPE_IDS,
    [value.children]
  );

  const onToggleItemCopyModal = useCallback(
    () => setShowCopyToNextPeriodModal(false),
    []
  );

  const { isAddingTag, setIsAddingTag, onClickAddTag } = useObjectiveAddTag();

  if (!isEditable && hasNoUsefulInformation(value)) {
    return null;
  }

  const typeBadge = (
    <ObjectiveTypeBadge
      objectiveTypeId={value.objective_type}
      organizationObjectiveTypes={organizationObjectiveTypes}
      collaborators={value.collaborators}
      isEditable={isEditable}
      onChangeType={onChangeType}
      userEditableObjectiveTypes={userEditableObjectiveTypes}
      childrenTypeIds={childrenTypeIds}
      parentTypeId={parentObjective?.objective_type}
    />
  );

  return (
    <>
      <ObjectiveItem
        hidden={hidden}
        key={value.key}
        aria-label={'container-objective-' + value.key}
      >
        <ObjectiveItemHead>
          <PersonalObjectiveItemDeleteModal
            isOpen={showDeleteModal}
            onClosed={() => setShowDeleteModal(false)}
            toggle={() => setShowDeleteModal(!showDeleteModal)}
            objective={value}
            onDelete={({ deleteAllChildren, removeForAllCollaborators }) =>
              deleteObjective({
                value,
                parentObjective,
                updateParentObjective,
                deleteAllChildren,
                removeForAllCollaborators,
              })
            }
            me={me}
            person={person}
          />
          <PersonalObjectiveItemCopyToNextPeriodModal
            isOpen={showCopyToNextPeriodModal}
            onClosed={onToggleItemCopyModal}
            toggle={onToggleItemCopyModal}
            objective={value}
            organizationObjectiveTypes={organizationObjectiveTypes}
            person={person}
          />
          <PersonalObjectiveItemDisconnectModal
            isOpen={showDisconnectModal}
            onClosed={() => setShowDisconnectModal(false)}
            toggle={() => setShowDisconnectModal(!showDisconnectModal)}
            objective={value}
            onDisconnect={() => disconnectFromParentObjective({ value })}
          />
        </ObjectiveItemHead>
        <ObjectiveItemBody>
          <ObjectiveItemHeaderRow wrap={inlineTypeBadge}>
            <div
              className="flex-grow-1"
              style={{
                minWidth: 150,
                flexBasis: inlineTypeBadge ? 0 : undefined,
              }}
            >
              <ObjectiveItemTitle
                isInPopover={isInPopover}
                showChildren={showChildren}
                fullWidth={hideScoresAndWeight && !isInPopover && !isEditable}
                hasChildren={(value?.children?.length ?? 0) > 0}
                onClick={() =>
                  (value?.children?.length ?? 0) > 0 &&
                  setShowChildren(!showChildren)
                }
                indent={indent}
              >
                {isEditable ? (
                  <TextareaAutosize
                    style={{
                      overflowY: 'hidden',
                    }}
                    className="form-control form-control-flush mt-2"
                    placeholder={formatMessage({
                      id: 'app.views.person.personal_objective_item.text_editor.placeholder',
                      defaultMessage: 'Write a key result',
                    })}
                    value={value.name}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      updateText({ e, value, parentObjective, setValue })
                    }
                    ref={value.ref}
                    autoFocus={value.isNew}
                    onKeyDown={(e) =>
                      updateTextKeyDown({
                        e,
                        addSibling,
                        value,
                        parentObjective,
                        setShowDeleteModal,
                        deleteObjective,
                        updateParentObjective,
                      })
                    }
                  />
                ) : (
                  <p
                    className={
                      'form-control form-control-flush mb-0 ' +
                      (value.matched ? 'fw-bold' : '')
                    }
                    role="listitem"
                    aria-label={'objective-' + value.key}
                    id={'input-' + value.key}
                  >
                    {showDescendantsCount && totalDescendants > 0 && (
                      <Badge className="bg-light rounded-pill me-2">
                        {totalDescendants}
                      </Badge>
                    )}
                    <ObjectiveText
                      value={value.name}
                      textToHighlight={textToHighlight}
                    />
                  </p>
                )}
              </ObjectiveItemTitle>
            </div>
            <ObjectiveItemActions>
              {isEditable && !hideScoresAndWeight && (
                <ObjectiveItemAddControls
                  isEditable={isEditable}
                  canAddChild={canAddChild}
                  setShowChildren={setShowChildren}
                  addChild={addChild}
                  value={value}
                  setValue={setValue}
                />
              )}
              {!hideScoresAndWeight && (
                <>
                  <ObjectiveCollaboratorsOrPlaceholder
                    objective={value}
                    disabled={!isEditable}
                    isIndividual={isIndividual}
                    isRootObjective={false}
                    onSave={(updatedChild) => {
                      setValue({
                        ...value,
                        collaborators: updatedChild.collaborators,
                      });
                    }}
                  />
                  <ObjectiveIndicator
                    disabled={!isEditable}
                    hidden={hidden}
                    objective={value}
                    parentObjective={parentObjective}
                    me={me}
                    onChange={(updatedObj) =>
                      updateIndicators({
                        updatedObj,
                        value,
                        parentObjective,
                        updateParentObjective,
                        setValue,
                      })
                    }
                    omitDateInputs={false}
                  />
                </>
              )}
              {showDescendantsBreakdown && (
                <ObjectivesBreakdownBar breakdown={descedantsStatusBreakdown} />
              )}
              {isEditable && !hideScoresAndWeight && (
                <ObjectiveItemMoreControls
                  isEditable={isEditable}
                  canAddChild={canAddChild}
                  setShowChildren={setShowChildren}
                  setShowDeleteModal={setShowDeleteModal}
                  setShowCopyToNextPeriodModal={setShowCopyToNextPeriodModal}
                  setShowDisconnectModal={setShowDisconnectModal}
                  addChild={addChild}
                  value={value}
                  setValue={setValue}
                  userEditableObjectiveTypes={userEditableObjectiveTypes}
                  organizationObjectiveTypes={organizationObjectiveTypes}
                  parentTypeId={parentObjective?.objective_type}
                  onChangeType={onChangeType}
                  onClickAddTag={onClickAddTag}
                />
              )}
            </ObjectiveItemActions>
            {inlineTypeBadge && <div>{typeBadge}</div>}
          </ObjectiveItemHeaderRow>
          <ObjectiveItemTagsRow>
            {!inlineTypeBadge && typeBadge}
            <ObjectiveTags
              tags={value.tags || []}
              isEditable={isEditable}
              isAdding={isAddingTag}
              setIsAdding={setIsAddingTag}
              onChangeTags={onChangeTags}
            />
            {value.related_to &&
              value.related_to.length > 0 &&
              !hideRelatedObjectives &&
              value.name?.length > 0 && (
                <LinkedObjectives
                  id={`linked-objectives-${objective.key}`}
                  textIfEmpty={formatMessage({
                    id: 'app.views.widget.inputs.person_objectives.linked_objectives.related_to.editing',
                    defaultMessage: 'link related',
                  })}
                  objectives={value.related_to}
                  owner_person={value.owner_person}
                  coverageStartDate={value.coverage_start_date}
                  coverageEndDate={value.coverage_end_date}
                  isEditable={false}
                  callback={(relatedObjectives) =>
                    updateRelatedObjectives({
                      value,
                      setValue,
                      relatedObjectives,
                      parentObjective,
                    })
                  }
                />
              )}
          </ObjectiveItemTagsRow>
        </ObjectiveItemBody>
      </ObjectiveItem>
      {value.children && (
        <>
          {value.children.map((child) => (
            <PersonalObjectiveItem
              hidden={!showChildren || hidden}
              isEditable={isEditable}
              isInPopover={isInPopover}
              hideScoresAndWeight={hideScoresAndWeight}
              hideRelatedObjectives={hideRelatedObjectives}
              key={child.key}
              objective={child}
              parentObjective={value}
              updateParentObjective={(updated) => {
                onChildChange(updated);
                updateParentObjective({
                  ...parentObjective,
                  // @ts-expect-error
                  children: parentObjective.children.map((child) =>
                    child.key !== updated.key ? child : updated
                  ),
                });
              }}
              currentOrganization={currentOrganization}
              currentProxyPerson={currentProxyPerson}
              me={me}
              person={person}
              indent={indent + 1}
              editableObjectiveIds={editableObjectiveIds}
              showDescendantsCount={showDescendantsCount}
              showDescendantsBreakdown={showDescendantsBreakdown}
              textToHighlight={textToHighlight}
              organizationObjectiveTypes={organizationObjectiveTypes}
              userEditableObjectiveTypes={userEditableObjectiveTypes}
              inlineTypeBadge={inlineTypeBadge}
            />
          ))}
          {isEditable && (
            <PersonalObjectiveAddItem
              hidden={!showChildren || hidden}
              indent={indent}
              onClick={() => addChild({ value, setValue, setShowChildren })}
              value={value}
            />
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  const { me, currentOrganization, currentProxyPerson, features } = state;

  return {
    me,
    currentOrganization,
    currentProxyPerson,
    features,
  };
};

export default connect(mapStateToProps)(React.memo(PersonalObjectiveItem));
